/**
 * © [2020] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { useDispatch, useSelector } from 'react-redux';

import CustomHeader from './customHeader';
import CustomLoadingCellRenderer from './CustomComponentsForTable/loadingSpinnerRenderer';
import DisplayErrorMessages from '../Errors/ErrorMessages';
import * as constantMessageData from '../Errors/constantMessageData';
import {
  handleCommaSeparatedAmount,
  handleOnColumnMoved,
  removeCommaForNumberFiled,
  removeDoubleQueFiled,
  replaceWhiteSpaceIntoString,
} from '../CommonUtilities/commonUtilities';
import { dispatchFilterAndSortFun, dispatchTableLoader } from './store/Actions';
import { checkNumber } from '../utilities/utilities';

ModuleRegistry.registerModules([CsvExportModule]);
const KEY_LEFT = 'ArrowLeft';
const KEY_RIGHT = 'ArrowRight';

const customProcessCellCallback = (params, isCSV = false) => {
  if (params.column.colDef.pdfExportOptions?.skipColumn) {
    return null;
  }
  let { value } = params;
  if (
    params.column.getColDef().valueFormatter &&
    params.column.getColDef().exportOption?.enableValueFormatter
  ) {
    const formattedValue = params.column.getColDef().valueFormatter({
      ...params,
      data: {
        ...params.data,
        ...params.node?.data,
      },
    });
    value = formattedValue;
  }
  if (
    params.column.getColDef().isAmount &&
    value !== 0 &&
    value !== '0.00' &&
    !value?.toString()?.includes('%')
  ) {
    value = Math.round(100 * value) / 100;
  }
  if (params.column?.colDef?.showDropDownVal) {
    value = params?.node?.data[params?.column?.colDef.dropDownValueCol];
    value = value === '-1' ? '' : value;
  }
  if (params.column?.colDef?.isTextColorRed) {
    value = removeCommaForNumberFiled(value);
  }
  if (
    value === null ||
    value === 'Invalid date' ||
    value === undefined ||
    value === '-1'
  ) {
    value = '';
  }
  if (params.column?.colDef?.exportZeroesTwo && value) {
    if (checkNumber(value)) {
      value = parseFloat(
        value?.toString()?.replace(',', '').trim()
      )?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      value = '0.00';
    }
  } else if (params.column?.colDef?.exportZeroesThree && value) {
    if (checkNumber(value)) {
      value = parseFloat(
        value?.toString()?.replace(',', '').trim()
      )?.toLocaleString('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
    } else {
      value = '0.000';
    }
  } else if (params.column?.colDef?.exportZeroesFour && value) {
    if (checkNumber(value)) {
      value = parseFloat(
        value?.toString()?.replace(',', '').trim()
      )?.toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    } else {
      value = '0.0000';
    }
  } else if (
    (params.column?.colDef?.exportZeroSix ||
      params.column?.colDef?.exportZeroesSix) &&
    value
  ) {
    if (checkNumber(value)) {
      value = parseFloat(
        value?.toString()?.replace(',', '').trim()
      )?.toLocaleString('en-US', {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      });
    } else {
      value = '0.000000';
    }
  } else if (
    value &&
    !isNaN(parseFloat(value)) &&
    params.column?.colDef?.excelAlignment
  ) {
    value = parseFloat(value?.toString()?.replace(',', '').trim());
  }
  if (
    !isCSV &&
    params.column?.colDef?.excelAlignment &&
    !params.column?.colDef?.printPrefixValue?.includes('$') &&
    !value?.toString()?.includes('$') &&
    value
  ) {
    value = `${value}~=~${params.column?.colDef.excelAlignment}`;
  }
  if (params.column?.colDef?.printPrefixValue) {
    if (params.column?.colDef?.isAmount && value === 0) {
      value = '0.00';
    }

    if (value?.toString()?.includes('%')) {
      value = `${value}`;
    } else {
      if (!isNaN(parseFloat(value))) {
        value = parseFloat(value).toFixed(2);
      }
      value =
        value === ''
          ? ''
          : `${params.column?.colDef?.printPrefixValue}${value}`;
    }
  }
  if (params.column?.colDef?.printPostfixValue) {
    if (params.column?.colDef?.isAmount && value === 0) {
      value = '0.00';
    }
    value = `${value}${params.column?.colDef?.printPostfixValue}`;
  }
  if (
    value?.toString() === 'Valid' &&
    params.column?.colDef?.showCheckboxIcon
  ) {
    value = '1';
  } else if (
    value?.toString() === 'Invalid' &&
    params.column?.colDef?.showCheckboxIcon
  ) {
    value = '0';
  } else if (
    params.column?.colDef?.showCheckboxIcon &&
    !params?.node?.data?.isBottomRow
  ) {
    value = value?.toString() === 'true' || value?.toString() === '1' ? 1 : 0;
  }
  if (
    isCSV &&
    value &&
    params.column?.colDef?.exportOption?.exportAsText &&
    typeof value === 'string'
  ) {
    value = `\t${value}`;
  } else if (isCSV && value && typeof value === 'string') {
    value = value.includes(',') ? `"${value}"` : value;
  }
  if ((params.value || params.value === 0) && !isNaN(params.value)) {
    if (params.column.getColDef().exportOption?.printWithCurrency)
      value = `${
        params.column.getColDef().exportOption?.printWithCurrency
      } ${value}`;
  }
  if (!value?.toString()?.includes('(')) {
    if (
      !isCSV &&
      (value?.toString()?.includes('$') ||
        params.column?.colDef.exportOption?.printWithCurrency)
    ) {
      const formattedValue = value?.split('$')[1]?.trim();
      value = handleCommaSeparatedAmount(
        value?.toString()?.includes('$') ? formattedValue : value
      );
    }
  } else if (
    !isCSV &&
    value?.toString()?.includes(')') &&
    value?.toString()?.includes('(')
  ) {
    const dollarVal = value?.split('$')[1]?.trim();
    const AmountVal = dollarVal?.split(')')[0]?.trim();
    value = `($ ${handleCommaSeparatedAmount(AmountVal)})`;
  }
  if (
    !isCSV &&
    params.column?.colDef?.excelAlignment &&
    value?.toString() === '0'
  ) {
    value = `${value}~=~${params.column?.colDef.excelAlignment}`;
  }
  return value;
};

const customProcessHeaderCallback = (params) => {
  if (params.column.colDef.pdfExportOptions?.skipColumn) {
    return null;
  }
  if (params.column.getColDef().headerName?.includes('.')) {
    return replaceWhiteSpaceIntoString(params.column.getColDef().headerName);
  }
  return params.column.getColDef().headerName;
};
const Grid = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const filterAndSortList = useSelector(
    (state) => state.sharedState?.TableGridReducer?.filterAndSortList
  );
  const gridRef = useRef();
  const [showError, setShowError] = React.useState(false);
  const [gridApi, setGridApi] = React.useState(gridRef);

  const {
    rowData,
    columnDefs,
    cellClickedListener,
    onCellValueChanged,
    gridHeight,
    frameworkComponents,
    defaultCol = {},
    overlayNoRowsTemplate,
    onGridReady,
    suppressNavigation,
    onSelect,
    singleClickEdit,
    noGlobalSort,
    onFilterChanged,
    onSortChanged,
    rowSelection,
    rowMultiSelectWithClick,
    onSelectionChanged,
    setTotal,
    isModalTable = false,
    navigateToNextCell,
    tabToNextCell,
    suppressRowClickSelection,
    enterMovesDown,
    suppressCellFocus,
    onCellFocused,
    childPopup,
    fileName,
    rowHeight = 38,
    headerHeight = 45,
    noRowPadding = false,
    pinnedBottomRowData = [],
    getRowStyle = () => {},
    hideColumnsInPdf,
    suppressQuotesOverride,
    skipColumnHeaders = false,
  } = props;
  const defaultColDef = useMemo(() => {
    return {
      editable: true,
      sortable: true,
      flex: 1,
      minWidth: 100,
      resizable: true,
      autoHeight: true,
      suppressColumnMoveAnimation: true,
      headerComponentParams: {
        menuIcon: 'fa-bars',
      },
      ...defaultCol,
      suppressKeyboardEvent: suppressNavigation,
      suppressKeyboardEvent: (params) => {
        // Left & Right arrow movement
        const { key } = params.event;
        if (
          (key === KEY_LEFT || key === KEY_RIGHT) &&
          params.node.rowIndex > -1
        ) {
          return true;
        }
      },
    };
  }, []);
  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: 'One moment please...',
    };
  }, []);
  React.useEffect(() => {
    if (columnDefs?.length > 0) {
      columnDefs.map((item, index) => {
        if (item.columnSpan) {
          for (let i = 1; i <= item.columnSpan; i += 1) {
            if (columnDefs[index + i]?.removeColumn) {
              columnDefs[index + i].hideColumnName = item.field;
            }
          }
        }
        return item;
      });
    }
  }, []);
  React.useEffect(() => {
    if (gridRef.current.api) {
      const count = gridRef.current.api.getDisplayedRowCount();
      if (
        count === 0 &&
        gridRef.current.api.filterManager &&
        gridRef.current.api.filterManager.activeColumnFilters &&
        gridRef.current.api.filterManager.activeColumnFilters.length > 0
      )
        setShowError(true);
      else setShowError(false);
      if (setTotal) setTotal(count);
    }
  });

  const components = useMemo(() => {
    return {
      agColumnHeader: CustomHeader,
    };
  }, []);
  useImperativeHandle(ref, () => ({
    clearFilters() {
      // eslint-disable-next-line no-unused-expressions
      if (gridRef) gridRef?.current?.api?.setFilterModel(null);
    },

    ExportCsv(fname) {
      setTimeout(() => dispatch(dispatchTableLoader(false)), 500);
      return gridRef.current.api.exportDataAsCsv({
        fileName: `${
          fname ? `${fname}.csv` : fileName ? `${fileName}.csv` : 'data.csv'
        }`,
        columnKeys: columnDefs
          ?.filter((x) => {
            if (hideColumnsInPdf) {
              return x.field !== hideColumnsInPdf[0];
            }
            return x;
          })
          .flatMap((column) => (column.children ? column.children : [column]))
          ?.filter((child) => !child.pdfExportOptions?.skipColumn)
          .map((child) => child.field),
        suppressQuotes: !suppressQuotesOverride,
        processCellCallback: (params) => {
          const tempObj = { ...params };
          if (
            tempObj?.value?.toString() === 'true' &&
            tempObj?.column?.colDef?.showCheckboxIcon
          ) {
            tempObj.value = 1;
          }
          if (tempObj.column?.colDef?.showDropDownVal) {
            tempObj.value =
              tempObj?.node?.data[tempObj?.column?.colDef.dropDownValueCol];
            tempObj.value = tempObj.value === '-1' ? '' : tempObj.value;
          }
          if (tempObj.column?.colDef?.printPrefixValue) {
            if (params.column?.colDef?.isAmount && tempObj.value === 0) {
              tempObj.value = '0.00';
            }
            if (tempObj.value?.toString()?.includes('%')) {
              tempObj.value = `${tempObj.value}`;
            } else {
              if (params.column?.colDef?.exportZeroesOne) {
                if (checkNumber(tempObj.value)) {
                  tempObj.value = parseFloat(tempObj.value)?.toFixed(1);
                } else {
                  tempObj.value = '0.0';
                }
              } else if (params.column?.colDef?.exportZeroesTwo) {
                if (checkNumber(tempObj.value)) {
                  tempObj.value = parseFloat(
                    tempObj.value?.toString()?.replace(',', '').trim()
                  )?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                } else {
                  tempObj.value = '0.00';
                }
              } else if (params.column?.colDef?.exportZeroesThree) {
                if (checkNumber(tempObj.value)) {
                  tempObj.value = parseFloat(tempObj.value).toFixed(3);
                } else {
                  tempObj.value = '0.000';
                }
              } else if (params.column?.colDef?.exportZeroesFour) {
                if (checkNumber(tempObj.value)) {
                  tempObj.value = parseFloat(tempObj.value).toFixed(4);
                } else {
                  tempObj.value = '0.0000';
                }
              } else if (
                params.column?.colDef?.exportZeroSix ||
                params.column?.colDef?.exportZeroesSix
              ) {
                if (checkNumber(tempObj.value)) {
                  tempObj.value = parseFloat(tempObj.value).toFixed(6);
                } else {
                  tempObj.value = '0.000000';
                }
              } else if (
                !isNaN(parseFloat(tempObj.value)) &&
                tempObj.value &&
                params.column?.colDef?.excelAlignment
              ) {
                tempObj.value = parseFloat(tempObj.value).toFixed(2);
              }

              tempObj.value =
                tempObj.value !== ''
                  ? `${tempObj.column?.colDef?.printPrefixValue}${tempObj.value}`
                  : '';
            }
          }
          if (tempObj.column?.colDef?.isTextColorRed) {
            tempObj.value = removeCommaForNumberFiled(tempObj.value || 0);
          }
          if (
            tempObj?.value?.toString() === 'Valid' &&
            tempObj?.column?.colDef?.showCheckboxIcon
          ) {
            tempObj.value = '1';
          } else if (
            tempObj?.value?.toString() === 'Invalid' &&
            tempObj?.column?.colDef?.showCheckboxIcon
          ) {
            tempObj.value = '0';
          } else if (
            tempObj.column?.colDef?.showCheckboxIcon &&
            !tempObj?.node?.data?.isBottomRow
          ) {
            tempObj.value =
              tempObj.value?.toString() === 'true' ||
              tempObj.value?.toString() === '1'
                ? 1
                : 0;
          }
          if (
            tempObj.value === null ||
            tempObj.value === 'Invalid date' ||
            tempObj.value === undefined ||
            tempObj.value === '-1'
          ) {
            tempObj.value = '';
          }
          if (
            (tempObj.value?.toString()?.includes('$') ||
              tempObj.column?.colDef.exportOption?.printWithCurrency) &&
            (tempObj.column?.colDef?.alignRight ||
              tempObj.column?.colDef.cellStyle?.textAlign === 'right')
          ) {
            const formattedValue = tempObj.value?.split('$')[1]?.trim();
            tempObj.value = `$ ${parseFloat(
              formattedValue?.toString()?.replace(',', '')?.trim()
            )?.toFixed(2)}`;
          }
          if (
            tempObj.value?.toString()?.includes(',') ||
            tempObj.value?.toString()?.includes('"')
          ) {
            tempObj.value = removeDoubleQueFiled(tempObj.value);
          }
          return customProcessCellCallback(tempObj, true);
        },
        processHeaderCallback: customProcessHeaderCallback,
        skipColumnHeaders,
        skipColumnGroupHeaders: false,
      });
    },
    ExportExcel() {
      return gridRef.current.api.getDataAsCsv({
        fileName: `${fileName ? `${fileName}.csv` : 'data.xlsx'}`,
        columnKeys: columnDefs
          ?.filter((x) => {
            if (hideColumnsInPdf) {
              return x.field !== hideColumnsInPdf[0];
            }
            return x;
          })
          .flatMap((column) => (column.children ? column.children : [column]))
          ?.filter((child) => !child.pdfExportOptions?.skipColumn)
          .map((child) => child.field),
        processCellCallback: customProcessCellCallback,
        processHeaderCallback: customProcessHeaderCallback,
        skipColumnHeaders,
        skipColumnGroupHeaders: false,
      });
    },
    addTBillRate() {
      return gridRef.current.api.setColumnDefs(columnDefs);
    },
  }));
  const loadingCellRenderer = useMemo(() => {
    return CustomLoadingCellRenderer;
  }, []);
  const onCellKeyDown = useCallback((e) => {
    const keyboardEvent = e?.event;
    const { key } = keyboardEvent;
    if (key?.length && (key === 'Enter' || key === ' ')) {
      gridRef.current.api.forEachNode((node) => {
        if (node.rowIndex === e.rowIndex) {
          node.setSelected(true);
        }
      });
      if (cellClickedListener) {
        cellClickedListener(e);
      } else if (onSelect) {
        onSelect(e);
      }
    }
  }, []);
  const handleOnGridReady = useCallback((params) => {
    if (params.api) {
      const columnToolPanel = params.api?.getToolPanelInstance('columns');
      if (
        columnToolPanel?.setColumnLayout &&
        columnDefs?.filter((x) => x.columnSpan)?.length > 0
      ) {
        columnToolPanel.setColumnLayout(
          columnDefs?.filter((x) => {
            return x.headerName !== '';
          })
        );
      }
      addFilterForTable(params);
    }
  }, []);
  const onFilterChangedGrid = (e) => {
    const gridName = `${window.location.pathname?.replace('/', '')}-${
      fileName?.includes(' ') ? fileName?.split(' ')?.join('') : fileName
    }`;
    if (
      (e?.api.getFilterModel() && Object.keys(e?.api?.getFilterModel()))
        ?.length > 0
    ) {
      dispatch(
        dispatchFilterAndSortFun({
          ...filterAndSortList,
          [gridName]: {
            ...filterAndSortList[gridName],
            activeURL: window.location.pathname,
            filter: e.api.getFilterModel(),
          },
        })
      );
    } else {
      dispatch(
        dispatchFilterAndSortFun({
          ...filterAndSortList,
          [gridName]: {
            ...filterAndSortList[gridName],
            activeURL: window.location.pathname,
            filter: [],
          },
        })
      );
    }
  };
  const onColumnPinned = useCallback(
    (e) => {
      const gridName = `${window.location.pathname?.replace('/', '')}-${
        fileName?.includes(' ') ? fileName?.split(' ')?.join('') : fileName
      }`;
      if (
        e?.columnApi?.getColumnState() &&
        Object.keys(e?.columnApi?.getColumnState())?.length > 0
      ) {
        dispatch(
          dispatchFilterAndSortFun({
            ...filterAndSortList,
            [gridName]: {
              ...filterAndSortList[gridName],
              activeURL: window.location.pathname,
              sort: e?.columnApi?.getColumnState(),
            },
          })
        );
      }
    },
    [filterAndSortList]
  );
  const onSortChangedGrid = useCallback(
    (e) => {
      const gridName = `${window.location.pathname?.replace('/', '')}-${
        fileName?.includes(' ') ? fileName?.split(' ')?.join('') : fileName
      }`;
      if (
        e?.columnApi?.getColumnState() &&
        Object.keys(e?.columnApi?.getColumnState())?.length > 0
      ) {
        dispatch(
          dispatchFilterAndSortFun({
            ...filterAndSortList,
            [gridName]: {
              ...filterAndSortList[gridName],
              activeURL: window.location.pathname,
              sort: e?.columnApi?.getColumnState(),
            },
          })
        );
      }
    },
    [filterAndSortList]
  );
  const addFilterForTable = React.useCallback(
    (params) => {
      const gridName = `${window.location.pathname?.replace('/', '')}-${
        fileName?.includes(' ') ? fileName?.split(' ')?.join('') : fileName
      }`;
      if (
        Object.keys(filterAndSortList)?.filter((x) => {
          return x === gridName;
        })?.length
      ) {
        const filterData = filterAndSortList[gridName]?.filter;
        const sortData = filterAndSortList[gridName]?.sort;
        if (params?.api && filterData) {
          setTimeout(() => {
            params.api.setFilterModel(filterData);
          }, 1000);
        }
        if (params?.columnApi && sortData) {
          params.columnApi.applyColumnState({
            state: sortData,
          });
        }
      }
    },
    [filterAndSortList]
  );

  return (
    <div
      className={`FRR-custom-table ${noRowPadding ? 'no-padding-row' : ''}  ${
        isModalTable
          ? 'modal-table'
          : rowData && rowData?.length > 0
          ? 'data-table'
          : 'empty-table'
      }`}
    >
      {showError && (
        <DisplayErrorMessages
          errorMessages={[constantMessageData.NO_DATA_FOUND_ERROR]}
        />
      )}
      <div
        className="ag-theme-alpine labeler_wrap"
        style={{ height: gridHeight || '400px' }}
      >
        <div className="clearfix" />
        <AgGridReact
          ref={gridRef}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          frameworkComponents={frameworkComponents}
          loadingCellRenderer={loadingCellRenderer}
          onGridReady={(params) => {
            setGridApi(params);
            if (onGridReady) {
              onGridReady(params);
            }
            handleOnGridReady(params);
          }}
          loadingCellRendererParams={loadingCellRendererParams}
          rowData={rowData}
          onColumnPinned={onColumnPinned}
          columnDefs={
            (
              gridApi.api?.getFilterModel() &&
              Object.keys(gridApi.api.getFilterModel())
            )?.length > 0 || noGlobalSort
              ? columnDefs
              : columnDefs?.map((item, key) => {
                  if (
                    key === 0 &&
                    !(
                      item?.showCheckboxIcon ||
                      item?.hideFilterIcon ||
                      item?.pdfExportOptions?.skipColumn ||
                      item?.headerName?.toLowerCase()?.includes('action') ||
                      (hideColumnsInPdf && hideColumnsInPdf?.length > 0)
                    )
                  ) {
                    if (item?.children?.length > 0) {
                      item.children[0].sort = 'asc';
                    }
                    item.sort = 'asc';
                  } else if (
                    key === 1 &&
                    !(columnDefs[0]?.sort === 'asc') &&
                    !item?.showCheckboxIcon
                  ) {
                    item.sort = 'asc';
                  } else if (
                    key === 2 &&
                    !(columnDefs[0]?.sort === 'asc') &&
                    !(columnDefs[1]?.sort === 'asc')
                  ) {
                    item.sort = 'asc';
                  } else {
                    item.sort = '';
                  }
                  return item;
                })
          }
          defaultColDef={defaultColDef}
          onCellDoubleClicked={cellClickedListener}
          onFilterChanged={(e) => {
            if (onFilterChanged) {
              onFilterChanged(e);
            }
            onFilterChangedGrid(e);
          }}
          accentedSort
          onSortChanged={(e) => {
            if (onSortChanged) {
              onSortChanged(e);
            }
            onSortChangedGrid(e);
          }}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          rowSelection={rowSelection || 'single'}
          rowMultiSelectWithClick={rowMultiSelectWithClick || false}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressPivotMode: true,
                  suppressValues: true,
                  suppressRowGroups: true,
                  suppressColumnSelectAll: true,
                },
              },
            ],
          }}
          onCellClicked={cellClickedListener || onSelect || (() => {})}
          components={components}
          suppressMenuHide
          overlayNoRowsTemplate={
            overlayNoRowsTemplate || 'There is no data to display.'
          }
          singleClickEdit={singleClickEdit || false}
          popupParent={childPopup ? null : document.querySelector('body')}
          suppressDragLeaveHidesColumns
          enableRangeSelection
          suppressMultiRangeSelection
          navigateToNextCell={navigateToNextCell}
          tabToNextCell={tabToNextCell}
          suppressRowClickSelection={suppressRowClickSelection}
          enterMovesDown={enterMovesDown}
          // suppressCellFocus={suppressCellFocus}
          onCellKeyDown={onCellKeyDown}
          onColumnMoved={() => handleOnColumnMoved(gridRef)}
          onCellFocused={onCellFocused}
          pinnedBottomRowData={pinnedBottomRowData}
          getRowStyle={getRowStyle}
          getContextMenuItems={[]}
        />
      </div>
    </div>
  );
});
export default Grid;
